import React, { useState, useMemo, useCallback } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";

interface MapProps {
  selectedProperty?: {
    location: {
      latitude: number;
      longitude: number;
      street?: string;
      city?: string;
      country?: string;
      zip?: string;
    };
  };
  zoom?: number;
}

const containerStyle = {
  width: "100%",
  height: "180px",
};

const HotelLocationMap: React.FC<MapProps> = ({
  selectedProperty,
  zoom = 17,
}) => {
  const { t } = useTranslation();
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);

  const { location } = selectedProperty || {
    location: { latitude: 0, longitude: 0 },
  };
  const center = useMemo(
    () => ({
      lat: Number(location.latitude),
      lng: Number(location.longitude),
    }),
    [location.latitude, location.longitude]
  );

  const onLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      setMap(mapInstance);
    },
    []
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const address = useMemo(() => {
    if (selectedProperty) {
      const { location } = selectedProperty;
      return `${location?.street}, ${location?.city}, ${location?.zip} ${location?.country}`;
    }
    return "";
  }, [selectedProperty]);

  const handleViewOnMap = useCallback(() => {
    if (selectedProperty?.location) {
      const { latitude, longitude } = selectedProperty.location;
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(url, "_blank");
    }
  }, [selectedProperty]);

  return (
    <div className="location-box">
      <header className="headingHead margin-reduced">
        <h2>{t("location")}</h2>
      </header>
      <div className="boxed">
        <div className="map-box">
          {!isLoaded ? (
            t("loading_map")
          ) : (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={zoom}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
            >
              <Marker position={center} />
            </GoogleMap>
          )}
        </div>
        <div className="content-box">
          <p>{address}</p>
          <span
            onClick={handleViewOnMap}
            style={{
              cursor: "pointer",
              color: "#007bff",
              textDecoration: "underline",
            }}
          >
            {t("view_on_a_map")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default HotelLocationMap;
